import { Overlay } from '@blueprintjs/core'
import React from 'react'

import {
  DateTimePickerPopover,
  IDateTimePickerPopoverProps,
} from 'browser/components/atomic-elements/atoms/date-picker/date-time-picker-popover'

import './_date-time-bottom-sheet.scss'

interface IDateTimeBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /**
   * Props forwarded to the DateTimePickerPopover rendered inside the sheet.
   * `hideFooter` + `inline` are injected by the sheet so the picker shows
   * only its picker controls (no nested card chrome, no Apply button); the
   * sheet provides its own Apply affordance in the header.
   */
  pickerProps: IDateTimePickerPopoverProps
}

export const DateTimeBottomSheet: React.FC<IDateTimeBottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  pickerProps,
}) => {
  const pickerRef = React.useRef<DateTimePickerPopover>(null)
  const [canApply, setCanApply] = React.useState(false)

  // Reset readiness each time the sheet opens so the Apply affordance starts
  // from the picker's own initial state rather than carrying stale value.
  React.useEffect(() => {
    if (!isOpen) setCanApply(false)
  }, [isOpen])

  const handleApply = () => {
    pickerRef.current?.apply()
  }

  return (
    <Overlay
      isOpen={isOpen}
      onClose={onClose}
      canEscapeKeyClose={true}
      canOutsideClickClose={true}
      hasBackdrop={true}
      className='c-dtBottomSheet-overlay'
      backdropClassName='c-dtBottomSheet-backdrop'
    >
      <div className='c-dtBottomSheet' role='dialog' aria-label={title}>
        <div className='c-dtBottomSheet-header'>
          <button
            type='button'
            className='c-dtBottomSheet-cancel'
            onClick={onClose}
          >
            Cancel
          </button>
          <span className='c-dtBottomSheet-title'>{title}</span>
          <button
            type='button'
            className='c-dtBottomSheet-apply'
            disabled={!canApply}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
        <div className='c-dtBottomSheet-content'>
          <DateTimePickerPopover
            ref={pickerRef}
            {...pickerProps}
            hideFooter={true}
            inline={true}
            onClose={onClose}
            onCanApplyChange={setCanApply}
          />
        </div>
      </div>
    </Overlay>
  )
}
